import $ from 'jquery';
import './style.scss';
import manageTableViews from './views-manager';
import registerColumnExpanderHandlers from './mobile-column-expander';
import { registerPaginationTableSorter } from '../pagination-nav/table-sorter';

const loadingClassName = 'comparison-table--loading';
const orderSwitchSelector = '.comparison-table__orderSwitch';
const sortByDropdownSelector = '#sortByDropdown__form';
const sortableListSelector = '.comparison-table__sortableItems';
const comparisonTableSelector = '.js-calculator-table, .js-comparison-table';
const comparisonTableWrapper = '.js-comparison-calculator, .comparison-calculator';
const isVisibleClassName = 'is-visible';
const SORTER_IDENTIFIER = {
  DEFAULT: 'js-tablesorter',
  PAGINATED_TABLE_SORTER: 'js-paginated-table-sorter',
};

function initialize($table) {
  manageTableViews($table.parents(comparisonTableWrapper));
  registerTableHandlers($table);
  registerColumnExpanderHandlers($table);
}

function getSortOrderTextIndicator({ isAscending, type = 'default' }) {
  const sortOrderIndicators = {
    default: {
      asc: 'low-high',
      desc : 'high-low',
    },
    text: {
      asc: 'A-Z',
      desc : 'Z-A',
    },
  };
  const textIndicator = sortOrderIndicators[type];
  const direction = isAscending ? 'asc' : 'desc';

  if (!textIndicator) {
    throw new Error(`A text indicator for type ${type} was not found`);
  }

  return textIndicator[direction];
}

function toggleColumnList({ currentTarget: orderSwitch }) {
  const $orderSwitch = $(orderSwitch);
  const $columnList = $orderSwitch
    .next(sortableListSelector);

  function outsideClickHandler({ currentTarget }) {
    if (currentTarget === orderSwitch) {
      return;
    }

    $columnList.toggleClass(isVisibleClassName);
  }

  $columnList.toggleClass(isVisibleClassName);

  const shouldListenForOutsideClick = $columnList.hasClass(isVisibleClassName);

  if (shouldListenForOutsideClick) {
    $(document).one('click', outsideClickHandler);
  }
}

function onColumnSort($table) {
  const $column = $table.find('th:not(.tablesorter-headerUnSorted)');

  const isAscending = $column.attr('aria-sort') === 'ascending';
  const type = $column.hasClass('comparison-table__name') ? 'text' : 'default';
  const sortOrderIndicator = getSortOrderTextIndicator({
    isAscending,
    type,
  });
  const columnName = `${$column.text().trim()} (${sortOrderIndicator})`;

  const $orderSwitch = $column
    .closest(comparisonTableSelector)
    .siblings(orderSwitchSelector);

  $orderSwitch.attr('data-sort-by', columnName);
  $.trackEvent('Table engine', 'Sort', columnName, undefined, true);
}

function createSortingDropdown(table) {
  const $table = $(table);
  const sortingHandlerType = $table.data('sortingHandlerType');

  function triggerSorting(getSelectedItem, postSortingCallback, { currentTarget }) {
    const $selectedListItem = getSelectedItem(currentTarget);

    const columnId = parseInt(
      $selectedListItem.attr('data-column-id'),
      10,
    );
    const columnName = $selectedListItem.attr('data-column-name');
    const sortDirection = $selectedListItem.attr('data-sort-direction');

    if ($table.hasClass(SORTER_IDENTIFIER.DEFAULT)) {
      $table.trigger('sorton', [[[columnId, sortDirection]]]);
    }

    if ($table.hasClass(SORTER_IDENTIFIER.PAGINATED_TABLE_SORTER)) {
      table.dispatchEvent(new CustomEvent('order-switch-click', {
        detail: { column: columnName, order: sortDirection, item: $selectedListItem },
      }));
    }

    postSortingCallback($selectedListItem);
  }

  function decorateItem($itemToDecorate, attachMarkIcon = false) {
    const $decoratedItem = $itemToDecorate.clone();
    const text = $decoratedItem.text();
    const type = $decoratedItem.attr('data-type');
    const isAscending = $decoratedItem.attr('data-sort-direction') === 'ascending';
    const postfix = getSortOrderTextIndicator({
      isAscending,
      type,
    });
    const itemText = `${text} (${postfix})`;

    $decoratedItem.html(itemText);

    if (attachMarkIcon) {
      const $markIcon = $('<i />').addClass('icon-tick icon__medium');

      $decoratedItem.prepend(
        $markIcon.clone(),
      );
    }

    return $decoratedItem;
  }

  function getDataAttributes(columnId, direction, itemType, columnName) {
    let data = {
      'data-column-id': columnId,
      'data-sort-direction': direction,
      'data-type': itemType,
    };

    if (columnName) {
      data = { ...data, 'data-column-name': columnName };
    }

    return data;
  }

  function insertSortableItem($list, tagName) {
    return (i, item) => {
      const $item = $(item);
      const isSortable = $item.hasClass('comparison-table__sortable');

      if (!isSortable) {
        return;
      }

      function createSortableItem(direction = 'ascending') {
        const itemType = $item.hasClass('comparison-table__name') ? 'text' : 'default';
        const columnName = $item.data('ajax-table-sorter-column') || '';
        const $sortableItem = $(`<${tagName} />`)
          .attr(getDataAttributes($item.index(), direction, itemType, columnName))
          .text($item.text().trim());

        if (tagName === 'li') {
          $sortableItem.attr('role', 'button');
        }

        return decorateItem($sortableItem);
      }

      const $sortableItemAsc = createSortableItem('ascending');
      const $sortableItemDesc = createSortableItem('descending');

      $list.append($sortableItemAsc);
      $list.append($sortableItemDesc);
    };
  }

  function insertSortableListItem($list) {
    return (i, item) => {
      const $item = $(item);
      const isSortable = $item.hasClass('comparison-table__sortable');

      function createSortableListItem(direction = 'ascending') {
        const itemType = $item.hasClass('comparison-table__name') ? 'text' : 'default';
        const columnName = $item.data('ajax-table-sorter-column') || '';
        const $sortableListItem = $('<li />')
          .attr({
            ...getDataAttributes($item.index(), direction, itemType, columnName),
            role: 'button',
          })
          .text($item.text().trim());

        return decorateItem($sortableListItem, true);
      }

      if (!isSortable) {
        return;
      }

      const $sortableListItemAsc = createSortableListItem('ascending');
      const $sortableListItemDesc = createSortableListItem('descending');

      $list.append($sortableListItemAsc);
      $list.append($sortableListItemDesc);
    };
  }

  function createSortableList() {
    const $sortableList = $('<ul />');
    const $sortables = $table.find('th:not([data-mobile="hide"])');

    function postSortingCallback($selectedListItem) {
      const isActiveClassName = 'is-active';

      $selectedListItem
        .addClass(isActiveClassName)
        .siblings()
        .removeClass(isActiveClassName);
    }

    function getSelectedElement(ctx) {
      return $(ctx);
    }

    $sortables.each(
      insertSortableListItem($sortableList, 'li'),
    );

    $sortableList
      .on('click', 'li', triggerSorting.bind(null, getSelectedElement, postSortingCallback))
      .addClass('comparison-table__sortableItems')
      .insertBefore($table);
  }

  function createSortableDropdown() {
    const sortableListName = 'sortableList';
    const $sortableList = $('<select />')
      .addClass('luna-select')
      .attr('name', sortableListName);

    const $sortableListLabel = $('<label>')
      .addClass('hide-mobile')
      .attr('for', sortableListName)
      .text('Sort by');

    const $sortableListWrapper = $('<div>')
      .addClass('table-sorter-wrapper')
      .append($sortableListLabel)
      .append($sortableList);

    const $sortables = $table.find('th:not([data-mobile="hide"])');

    function postSortingCallback() {
    }

    function getSelectedElement(ctx) {
      return $(ctx.options[ctx.selectedIndex]);
    }

    $sortables.each(
      insertSortableItem($sortableList, 'option'),
    );

    $sortableList
      .on('change', triggerSorting.bind(null, getSelectedElement, postSortingCallback));

    $sortableListWrapper.insertBefore($table);
  }

  switch (sortingHandlerType) {
    case 'dropdown':
      createSortableDropdown();
      break;

    default:
      createSortableList();
  }
}

function onLoaded(
  { target: table },
  response = null,
) {
  const $table = $(table);
  const $tbody = $table.find('tbody');

  $table.removeClass(loadingClassName);

  if (response && response.html) {
    $tbody.html(response.html);
  }

  const $tooltips = $table.find('[rel="popover"]');

  $table
    .trigger('update')
    .trigger('sorton');

  $tooltips
    .trigger('loadpopover');
}

function onLoading({ target: table }) {
  const $table = $(table);
  $table.addClass(loadingClassName);
}

function onTableInitialized({ target: table }) {
  const $table = $(table);
  const $calculator = $table.parents(comparisonTableWrapper);
  const predefinedSorting = $table.data('sorting');

  if (predefinedSorting) {
    const sortingHandlerType = $table.data('sortingHandlerType');
    const [columnId, sortDirection] = predefinedSorting;
    const $sortingListItem = $calculator
      .find(`[data-column-id='${columnId}']`)
      .eq(sortDirection);

    switch (sortingHandlerType) {
      case 'dropdown':
        $sortingListItem.prop('selected', true)
          .parent()
          .trigger('change');
        break;

      default:
        $sortingListItem.click();
    }
    $table.trigger('sorton', [[[columnId, sortDirection]]]);
  }
}

function resetShownRows($table) {
  const $loadMoreBtn = $table.siblings('.js-comparison-table__loadMore');
  const $paginationBtn = $table.siblings('.js-comparison-table__paginate');

  if ($table.data('load-more-rows') && $loadMoreBtn.is(':visible')) {
    // Tables with pagination (show default number of rows)
    $table.trigger('reset-shown-rows');
  }

  if ($paginationBtn.length) {
    $paginationBtn.show();
  }
}

function registerEventListeners() {
  $(document)
    .on('tablesorter-initialized', comparisonTableSelector, onTableInitialized)
    .on('sortEnd', comparisonTableSelector, (event) => {
      // where sortEnd is called after table has been updated / rows are sorted
      const $table = $(event.currentTarget);
      onColumnSort($table);
      resetShownRows($table);
    })
    .on('click', orderSwitchSelector, toggleColumnList)
    .on('change', sortByDropdownSelector, handleSortByDropdownClick);
}

function getColumnIdByColumnName(columnName) {
  const $columnElements = $.find(`.tablesorter-header[aria-label^='${columnName}']`);

  const $columnElement = getFilteredColumn($columnElements);

  if (!$columnElement) {
    return undefined;
  }

  return $($columnElement).attr('data-column');
}

function getFilteredColumn(columns) {
  const $filteredColumns = columns.filter(item => (item.dataset.desktop !== 'hide'));

  return $filteredColumns[0];
}

function handleSortByDropdownClick() {
  const selectDropdown = document.getElementById('sortBy');
  const selectedOptionName = selectDropdown.options[selectDropdown.selectedIndex].value;
  const selectedOptionNameTrimmed = selectedOptionName.split(' (')[0];
  const sortDirection = selectDropdown.options[selectDropdown.selectedIndex].getAttribute('data-sort-direction');
  const columnId = getColumnIdByColumnName(selectedOptionNameTrimmed);
  const $table = $($.find('.js-calculator-table, .js-comparison-table')[0]);

  if (columnId) {
    if ($table.hasClass(SORTER_IDENTIFIER.DEFAULT)) {
      $table.trigger('sorton', [[[columnId, sortDirection]]]);
    }

    if ($table.hasClass(SORTER_IDENTIFIER.PAGINATED_TABLE_SORTER)) {
      table.dispatchEvent(new CustomEvent('order-switch-click', {
        detail: { column: columnName, order: sortDirection },
      }));
    }
  }
}

function registerTableHandlers($table) {
  $table
    .on('onLoading', onLoading)
    .on('onLoaded', onLoaded);
}

function createTableSorting($table) {
  const [table] = $table;
  const [tableWrapper] = $table.closest(comparisonTableWrapper);
  if (!table || !tableWrapper) return;

  createSortingDropdown(table);

  const { paginationNav: isPaginationEnabled } = table.dataset || {};
  if (isPaginationEnabled &&
    table.classList &&
    table.classList.contains(SORTER_IDENTIFIER.PAGINATED_TABLE_SORTER)
  ) {
    registerPaginationTableSorter(table, tableWrapper);
  }
}

export {
  createTableSorting,
  registerEventListeners,
  initialize,
};
